import React from 'react';
import { Card, Group, Progress, Stack, Text, Title } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { ScoreDistribution } from '../../types';

interface DistributionChartProps {
  distributions: ScoreDistribution[];
}

const RATING_COLORS: Record<number, string> = {
  1: 'red',
  2: 'orange',
  3: 'yellow',
  4: 'teal',
  5: 'green',
};

export const DistributionChart: React.FC<DistributionChartProps> = ({ distributions }) => {
  const { t } = useTranslation();

  if (distributions.length === 0) return null;

  return (
    <Card withBorder radius="md" p="lg">
      <Title order={4} mb="md">
        {t('review360.distribution')}
      </Title>
      <Stack gap="md">
        {distributions.map((dist) => {
          const total = dist.ratings.reduce((sum, count) => sum + count, 0);

          return (
            <Stack key={dist.sectionTitle} gap={4}>
              <Text size="sm" fw={500}>
                {dist.sectionTitle}
              </Text>
              <Progress.Root size="xl">
                {dist.ratings.map((count, index) => {
                  const ratingValue = index + 1;
                  const percentage = total > 0 ? (count / total) * 100 : 0;
                  if (percentage === 0) return null;
                  return (
                    <Progress.Section
                      key={ratingValue}
                      value={percentage}
                      color={RATING_COLORS[ratingValue]}
                    >
                      <Progress.Label>{ratingValue}</Progress.Label>
                    </Progress.Section>
                  );
                })}
              </Progress.Root>
              <Group gap="md">
                {dist.ratings.map((count, index) => {
                  const ratingValue = index + 1;
                  return (
                    <Text key={ratingValue} size="xs" c="dimmed">
                      {t(`review360.ratingLabels.${ratingValue}`)}: {count}
                    </Text>
                  );
                })}
              </Group>
            </Stack>
          );
        })}
      </Stack>
    </Card>
  );
};
